import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import logodark from "../../assets/images/logo-dark.png";
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
} from "reactstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { verifyOTPUserAPI, verifyOTPEmployeeAPI, requestPasswordResetUserAPI, requestPasswordResetEmployeeAPI } from "../../helpers/api.ts";

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [resendingOTP, setResendingOTP] = useState(false);
  const [timerKey, setTimerKey] = useState(0);

  // Get email and userType from location state
  const email = location.state?.email;
  const userType = location.state?.userType || "user";

  // OTP expiration time: 10 minutes (600000 milliseconds)
  const OTP_EXPIRATION_TIME = 10 * 60 * 1000;

  useEffect(() => {
    // Redirect if email is not provided
    if (!email) {
      navigate(userType === "employee" ? "/employee-forgot-password" : "/forgot-password");
    } else {
      // Check OTP expiration timer
      const otpTimestamp = localStorage.getItem(`otp_timestamp_${email}`);
      if (otpTimestamp) {
        const updateTimer = () => {
          const startTime = parseInt(otpTimestamp, 10);
          const elapsed = Date.now() - startTime;
          const remaining = OTP_EXPIRATION_TIME - elapsed;
          
          if (remaining <= 0) {
            setTimeRemaining(0);
            setIsExpired(true);
          } else {
            setTimeRemaining(remaining);
            setIsExpired(false);
          }
        };
        
        // Update immediately
        updateTimer();
        
        // Update every second
        const interval = setInterval(updateTimer, 1000);
        
        return () => clearInterval(interval);
      }
    }
  }, [email, navigate, userType, timerKey]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("Please Enter OTP")
        .length(6, "OTP must be 6 digits"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        
        const response = userType === "employee" 
          ? await verifyOTPEmployeeAPI(email, values.otp)
          : await verifyOTPUserAPI(email, values.otp);
        
        if (response.success) {
          setSuccessMessage(response.message);
          // Clear OTP timestamp on successful verification
          localStorage.removeItem(`otp_timestamp_${email}`);
          // Navigate to reset password page after 1 second
          setTimeout(() => {
            navigate("/reset-password", { state: { email, userType } });
          }, 1000);
        } else {
          // Handle case where response.success is false
          const errorMsg = response.message || "Invalid OTP. Please try again.";
          setErrorMessage(errorMsg);
          
          // If OTP is expired, show resend option
          if (errorMsg.toLowerCase().includes("expired") || errorMsg.toLowerCase().includes("not found")) {
            setIsExpired(true);
            setTimeRemaining(0);
          }
        }
      } catch (error) {
        if (error.response) {
          const { data } = error.response;
          const errorMsg = data.message || data.error || "Invalid OTP. Please try again.";
          setErrorMessage(errorMsg);
          
          // If OTP is expired, show resend option
          if (errorMsg.toLowerCase().includes("expired") || errorMsg.toLowerCase().includes("not found")) {
            setIsExpired(true);
            setTimeRemaining(0);
          }
        } else {
          setErrorMessage("Unable to connect to the server. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const handleResendOTP = async () => {
    try {
      setResendingOTP(true);
      setErrorMessage("");
      setSuccessMessage("");
      
      const response = userType === "employee" 
        ? await requestPasswordResetEmployeeAPI(email)
        : await requestPasswordResetUserAPI(email);
      
      if (response.success) {
        setSuccessMessage("OTP has been resent successfully!");
        // Reset OTP timestamp
        const otpTimestamp = Date.now();
        localStorage.setItem(`otp_timestamp_${email}`, otpTimestamp.toString());
        setIsExpired(false);
        // Force timer to restart by updating timerKey
        setTimerKey(prev => prev + 1);
      }
    } catch (error) {
      if (error.response) {
        const { data } = error.response;
        setErrorMessage(
          data.message || data.error || "Failed to resend OTP. Please try again."
        );
      } else {
        setErrorMessage("Unable to connect to the server. Please try again.");
      }
    } finally {
      setResendingOTP(false);
    }
  };

  useEffect(() => {
    document.body.className = "bg-pattern";
    return () => {
      document.body.className = "";
    };
  }, []);

  if (!email) {
    return null; // Will redirect in useEffect
  }

  return (
    <React.Fragment>
      <div className="bg-overlay"></div>
      <div className="account-pages my-5 pt-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} xl={4}>
              <Card>
                <CardBody className="p-4">
                  <div>
                    <div className="text-center">
                      <Link to="/">
                        <img
                          src={logodark}
                          alt="dark logo"
                          height="100"
                          className="auth-logo logo-dark mx-auto"
                        />
                        <img
                          src={logodark}
                          alt="light logo"
                          height="100"
                          className="auth-logo logo-light mx-auto"
                        />
                      </Link>
                    </div>
                    <h4 className="font-size-18 text-muted mt-2 text-center">
                      Verify OTP
                    </h4>
                    <p className="mb-3 text-center">
                      Enter the 6-digit OTP sent to <strong>{email}</strong>
                    </p>
                    {timeRemaining !== null && !isExpired && (
                      <p className="mb-3 text-center text-muted">
                        <small>
                          OTP expires in:{" "}
                          <strong>
                            {Math.floor(timeRemaining / 60000)}:
                            {String(Math.floor((timeRemaining % 60000) / 1000)).padStart(2, "0")}
                          </strong>
                        </small>
                      </p>
                    )}
                    {isExpired && (
                      <Alert color="warning" className="mb-3">
                        <div className="text-center">
                          <small>OTP has expired. Please request a new OTP.</small>
                        </div>
                      </Alert>
                    )}
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {errorMessage && (
                        <Alert color="danger">
                          <div className="text-center">{errorMessage}</div>
                        </Alert>
                      )}
                      {successMessage && (
                        <Alert color="success">
                          <div className="text-center">{successMessage}</div>
                        </Alert>
                      )}
                      <Row>
                        <Col md={12}>
                          <div className="mb-4">
                            <Label className="form-label">OTP</Label>
                            <Input
                              name="otp"
                              className="form-control"
                              placeholder="Enter 6-digit OTP"
                              type="text"
                              maxLength={6}
                              onChange={(e) => {
                                // Only allow numbers
                                const value = e.target.value.replace(/\D/g, '');
                                validation.setFieldValue('otp', value);
                              }}
                              onBlur={validation.handleBlur}
                              value={validation.values.otp || ""}
                              invalid={
                                validation.touched.otp &&
                                validation.errors.otp
                              }
                            />
                            {validation.touched.otp &&
                              validation.errors.otp && (
                                <FormFeedback type="invalid">
                                  {validation.errors.otp}
                                </FormFeedback>
                              )}
                          </div>
                          <div className="d-grid mt-4">
                            <button
                              className="btn btn-primary waves-effect waves-light"
                              type="submit"
                              disabled={loading || isExpired}
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Verifying...
                                </>
                              ) : (
                                "Verify OTP"
                              )}
                            </button>
                          </div>
                          {isExpired && (
                            <div className="d-grid mt-3">
                              <button
                                className="btn btn-outline-primary waves-effect waves-light"
                                type="button"
                                onClick={handleResendOTP}
                                disabled={resendingOTP}
                              >
                                {resendingOTP ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    Resending...
                                  </>
                                ) : (
                                  "Resend OTP"
                                )}
                              </button>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Form>
                    <div className="text-center mt-4">
                      <Link 
                        to={userType === "employee" ? "/employee-forgot-password" : "/forgot-password"} 
                        className="text-muted"
                      >
                        <i className="mdi mdi-arrow-left"></i> Back
                      </Link>
                    </div>
                    <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-2 mb-4 text-center mt-4">
                      <Link to="/privacy-policy" className="text-muted">
                        Privacy Policy
                      </Link>
                      <span className="text-muted d-none d-md-inline">|</span>
                      <Link to="/terms-conditions" className="text-muted">
                        Terms & Conditions
                      </Link>
                      <span className="text-muted d-none d-md-inline">|</span>
                      <Link to="/contact-us" className="text-muted">
                        Contact Us
                      </Link>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-5 text-center">
                <p className="text-white-50">
                  © {new Date().getFullYear()} Crafted with{" "}
                  <i className="mdi mdi-heart text-danger"></i> by Govindani
                  Infotech Pvt. Ltd.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

OTPVerification.propTypes = {
  error: PropTypes.string,
};

export default OTPVerification;

